import { inject, Injectable, signal } from '@angular/core';
import { from, map, Observable, switchMap, tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AuthService } from '@brightside/core/auth';
import { AmplitudeService } from '@brighthub/logging';
import { ClientSessionService } from '@brighthub/uisession';
import { DialogService } from 'primeng/dynamicdialog';
import {
  ClientProfileSection,
  ClientProfileItem,
} from './client-profile.model';
import { ClientProfileService } from './client-profile.service';
import {
  ClientProfileSectionEditDialogComponent,
  type SectionEditDialogData,
  type SectionEditDialogResult,
} from './client-profile-section-edit-dialog/client-profile-section-edit-dialog.component';

function itemEditKey(sectionId: string, item: ClientProfileItem): string {
  return `${sectionId ?? 'section'}-${item.key ?? item.label}`;
}

/**
 * Service responsible for opening the section-edit dialog and persisting edits.
 * Holds local edit state (editValues) and calls the profile save API on save.
 */
@Injectable()
export class ClientProfileEditService {
  private _session = inject(ClientSessionService);
  private _profileService = inject(ClientProfileService);
  private _dialogService = inject(DialogService);
  private _messageService = inject(MessageService);
  private _auth = inject(AuthService);
  private _amplitude = inject(AmplitudeService);

  /** Local edit state: key (e.g. sectionId-itemKey) -> current value for editable fields. */
  readonly editValues = signal<Record<string, string | number>>({});

  /** Open the section-edit dialog. Dialog save uses saveProfileSection; cancel reverts this section in editValues. */
  openSectionEdit(section: ClientProfileSection): void {
    const guid = this._session.selectedSession()?.subjectData?.guid ?? null;
    if (!guid) return;

    const sectionId = section.id ?? section.title;
    const initialValues: Record<string, string | number> = {};
    const current = this.editValues();
    for (const item of section.items) {
      if (item.editable) {
        const key = itemEditKey(sectionId, item);
        const v = current[key];
        if (v !== undefined) initialValues[key] = v;
        else if (item.value != null) initialValues[key] = item.value;
      }
    }

    const save$ = (values: Record<string, string | number>) =>
      this.saveSection(guid, sectionId, values, initialValues);

    const data: SectionEditDialogData = { section, initialValues, save$ };
    const ref = this._dialogService.open(
      ClientProfileSectionEditDialogComponent,
      {
        header: 'Edit ' + section.title,
        width: '28rem',
        modal: true,
        closable: true,
        data,
      }
    );

    ref.onClose.subscribe((result: SectionEditDialogResult | undefined) => {
      if (result?.cancelled) {
        this.editValues.update((m) => {
          const next = { ...m };
          for (const item of section.items) {
            if (item.editable) {
              const key = itemEditKey(sectionId, item);
              delete next[key];
            }
          }
          return next;
        });
      }
      // If saved, editValues are already updated inside saveSection when the observable completes.
    });
  }

  /** Persist section edits via API, update editValues on success, show toast, and log Amplitude. */
  saveSection(
    guid: string,
    sectionId: string,
    values: Record<string, string | number>,
    previousValues: Record<string, string | number>
  ): Observable<unknown> {
    return this._profileService.saveProfileSection(guid, sectionId, values).pipe(
      tap(() => {
        this.editValues.update((m) => ({ ...m, ...values }));
        this._messageService.add({
          severity: 'success',
          summary: 'Saved',
          detail: 'Updates saved successfully.',
        });
      }),
      switchMap(() => from(this._auth.getUser())),
      tap((user) => {
        const changes = Object.keys(values).map((key) => ({
          key,
          previous_value: previousValues[key] ?? null,
          current_value: values[key],
        }));
        this._amplitude.logEvent('client_profile_section_saved', {
          account_guid: guid,
          okta_user_id: user.sub ?? '',
          okta_user_email: (user as { email?: string }).email ?? '',
          section_id: sectionId,
          changes,
        });
      }),
      map(() => undefined)
    );
  }
}
